import { api } from '@/lib/api'

/**
 * Thin Plaid API wrapper that proxies through OUR backend, never
 * Plaid directly. The backend already handles token exchange,
 * KMS-encryption of access tokens, and webhook signature checking
 * — duplicating any of that here would be a security regression.
 *
 * OAuth on web: institutions like BofA / Chase require an OAuth
 * redirect URI. The current MVP DOESN'T configure a web-specific
 * redirect — most non-OAuth banks (smaller institutions, credit
 * unions, sandbox) work without one. A follow-up will add the
 * redirect handler at /plaid-oauth and pass the appropriate URI
 * via createLinkToken's body.
 */

export interface LinkTokenResponse {
  linkToken: string
  expiration: string
}

export interface ExchangeResponse {
  itemId: string
  accountIds: string[]
  budgetAccountIds: string[]
}

export interface RefreshItemResult {
  itemId: string
  ok: boolean
  added?: number
  modified?: number
  removed?: number
  error?: string
}

export interface RefreshResponse {
  results: RefreshItemResult[]
}

/**
 * Web's OAuth redirect URI. When users link an OAuth bank (BofA,
 * Chase), Plaid bounces them through the bank's site and back here.
 * The /plaid-oauth route reads `oauth_state_id` + `linkToken` from
 * sessionStorage and re-opens Plaid Link with `receivedRedirectUri`
 * so the flow resumes from where it left off.
 *
 * Pre-prod: this URL is the prod custom domain. Staging uses a
 * different subdomain (budget-staging.crawfishlabs.ai). Local dev
 * doesn't get OAuth — Plaid rejects http localhost — but non-OAuth
 * banks (sandbox + many credit unions) work without any redirect.
 */
function webRedirectUri(): string {
  const origin = window.location.origin
  // If we're already on the custom domain, mirror it. Otherwise
  // fall back to prod — local dev hits the redirect through the
  // prod URL, which is fine because Plaid pops a window that
  // navigates THIS browser anyway (the localStorage state is
  // local). For truly local OAuth testing you'd need a tunneled
  // HTTPS hostname registered with Plaid.
  if (origin.startsWith('https://budget') && origin.includes('crawfishlabs.ai')) {
    return `${origin}/plaid-oauth`
  }
  return 'https://budget.crawfishlabs.ai/plaid-oauth'
}

export async function createLinkToken(): Promise<LinkTokenResponse> {
  return api.post<LinkTokenResponse>('/plaid/link/token/create', {
    redirectUri: webRedirectUri(),
  })
}

export async function exchangePublicToken(
  publicToken: string,
  metadata: Record<string, string> = {},
): Promise<ExchangeResponse> {
  return api.post<ExchangeResponse>('/plaid/item/exchange', {
    publicToken,
    linkSessionMetadata: metadata,
  })
}

export async function refreshAllPlaidItems(): Promise<RefreshResponse> {
  return api.post<RefreshResponse>('/plaid/refresh')
}
